import { useState } from "react";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { MdOpenInNew, MdBookmark, MdBookmarkBorder } from "react-icons/md";
import { FiChevronLeft, FiChevronRight, FiZoomIn } from "react-icons/fi";
import { FaRegClock, FaUserTie } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";


const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.15); }
`;

// ---------- Wrapper ----------
const CloseModalButton = styled.div`
      position: absolute;
      top: 16px; right: 16px;
      background: rgba(0,0,0,0.6);
      color: white;
      width: 38px; height: 38px;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-size: 1.1em;
      cursor: pointer;
      border: 1px solid rgba(255,255,255,0.2);
      z-index: 1001;
      transition: background 0.2s;

      &:hover { background: rgba(199,112,240,0.7); }
    }
  `
const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const ProjectCardWrapper = styled.div`
  height: 100%; /* ← fills whatever grid/flex cell it lives in */

  .project-card-view {
    background: rgba(15, 10, 30, 0.6);
    border: 1px solid rgba(199, 112, 240, 0.18);
    border-radius: 18px;
    overflow: hidden;
    height: 100%;           /* ← equal height key */
    display: flex;
    flex-direction: column;
    transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;
    backdrop-filter: blur(8px);

    &:hover {
      border-color: rgba(199, 112, 240, 0.55);
      box-shadow: 0 8px 40px rgba(199, 112, 240, 0.18);
      transform: translateY(-4px);
    }
  }

  &.featured .project-card-view {
    border-color: rgba(255, 200, 60, 0.45);
    box-shadow: 0 0 28px rgba(255, 185, 0, 0.15), 0 4px 24px rgba(255, 185, 0, 0.1);

    &:hover {
      border-color: rgba(255, 200, 60, 0.85);
      box-shadow: 0 0 42px rgba(255, 185, 0, 0.3), 0 8px 40px rgba(255, 185, 0, 0.2);
      transform: translateY(-6px);
    }
  }

  .featured-ribbon {
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 3px;
    background: linear-gradient(90deg, #f59e0b, #fbbf24, #f59e0b, #fde68a, #f59e0b);
    background-size: 200% auto;
    animation: ${shimmer} 2.5s linear infinite;
    z-index: 20;
  }

  /* ── Image Wrapper ── */
  .project-img-wrapper {
    position: relative;
    height: 210px;
    flex-shrink: 0;
    overflow: hidden;
    background: #0f0a1e;

    img.project-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      transition: transform 0.5s ease, filter 0.3s ease;
    }

    /* Hover overlay */
    .img-overlay {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.55);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 14px;
      opacity: 0;
      transition: opacity 0.3s ease;

      .overlay-btn {
        width: 46px;
        height: 46px;
        border-radius: 50%;
        border: 2px solid rgba(255,255,255,0.7);
        background: rgba(255,255,255,0.1);
        color: white;
        font-size: 1.2em;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        backdrop-filter: blur(4px);
        transition: background 0.2s, border-color 0.2s, transform 0.2s;

        &:hover {
          background: rgba(199, 112, 240, 0.7);
          border-color: #c770f0;
          transform: scale(1.1);
        }
      }
    }

    &:hover img.project-img {
      transform: scale(1.06);
      filter: brightness(0.75);
    }

    &:hover .img-overlay {
      opacity: 1;
    }

    .mini-arrow {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      background: rgba(0,0,0,0.55);
      color: white;
      border-radius: 50%;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      font-size: 1em;
      border: 1px solid rgba(255,255,255,0.2);
      transition: background 0.2s;
      z-index: 5;

      &.left  { left: 10px; }
      &.right { right: 10px; }
      &:hover { background: rgba(199,112,240,0.7); }
    }

    .img-dots {
      position: absolute;
      bottom: 8px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 5px;
      z-index: 5;

      .dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: rgba(255,255,255,0.4);
        transition: background 0.2s, transform 0.2s;
        cursor: pointer;

        &.active {
          background: #c770f0;
          transform: scale(1.4);
        }
      }
    }

    .bookmark-btn {
      position: absolute;
      top: 12px;
      right: 12px;
      z-index: 10;
      background: rgba(0,0,0,0.55);
      border: none;
      color: white;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      font-size: 1.15em;
      backdrop-filter: blur(4px);
      transition: background 0.2s, color 0.2s;

      &.saved {
        color: #c770f0;
        animation: ${pulse} 0.4s ease;
      }

      &:hover { background: rgba(199,112,240,0.5); }
    }

    .status-badge {
      position: absolute;
      top: 12px;
      left: 12px;
      z-index: 10;
      padding: 4px 10px;
      border-radius: 20px;
      font-size: 0.7em;
      font-weight: 600;
      letter-spacing: 0.5px;
      text-transform: uppercase;

      &.live    { background: rgba(34,197,94,0.2);  color: #4ade80; border: 1px solid rgba(74,222,128,0.3); }
      &.wip     { background: rgba(251,191,36,0.2); color: #fbbf24; border: 1px solid rgba(251,191,36,0.3); }
      &.archived{ background: rgba(148,163,184,0.2);color: #94a3b8; border: 1px solid rgba(148,163,184,0.3);}
      &.featured{ background: rgba(245,158,11,0.15); color: #fbbf24; border: 1px solid rgba(245,158,11,0.5); letter-spacing: 1px; }
    }
  }

  /* ── Card Body ── */
  .card-body {
    padding: 22px !important;
    display: flex;
    flex-direction: column;
    flex: 1;           /* ← fills remaining height so all bodies are equal */
  }

  .card-title {
    font-size: 1.25em;
    font-weight: 700;
    margin-bottom: 6px;
    background: linear-gradient(45deg, #c770f0, #ffffff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1.3;
  }

  .card-position-gradient {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.88em;
    color: #c770f0;
    margin-bottom: 12px;
    padding: 6px 12px;
    background: rgba(199,112,240,0.08);
    border-radius: 8px;
    border-left: 3px solid #c770f0;
    font-weight: 500;
  }

  .project-meta {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 10px;
    font-size: 0.8em;
    color: #9ca3af;

    .meta-item {
      display: flex;
      align-items: center;
      gap: 5px;

      svg { color: #c770f0; }
    }
  }

  .card-text {
    color: #9ca3af;
    line-height: 1.65;
    font-size: 0.88em;
    margin-bottom: 14px;
    flex-grow: 1;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .tech-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 16px;

    .tech-tag {
      font-size: 0.72em;
      padding: 3px 9px;
      border-radius: 20px;
      background: rgba(199,112,240,0.1);
      color: #c770f0;
      border: 1px solid rgba(199,112,240,0.25);
      font-weight: 500;
      letter-spacing: 0.3px;
      transition: background 0.2s, color 0.2s;
      cursor: default;

      &:hover {
        background: rgba(199,112,240,0.25);
        color: #fff;
      }
    }
  }

  .project-buttons {
    display: flex;
    gap: 8px;
    margin-top: auto;

    .btn {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 5px;
      font-size: 0.85em;
      padding: 9px 12px;
      border-radius: 10px;
      font-weight: 500;
      transition: all 0.25s ease;

      &.btn-primary {
        border: none;

        &:hover {
      
          box-shadow: 0 4px 15px rgba(199,112,240,0.4);
        }
      }

      &.btn-outline {
        background: transparent;
        border: 1px solid rgba(199,112,240,0.4);
        color: #c770f0;

        &:hover {
          background: rgba(199,112,240,0.12);
          border-color: #c770f0;
        }
      }
    }
  }

  /* ── Fullscreen Modal ── */
  .image-modal {
    .modal-dialog { max-width: 92vw; margin: 20px auto; }
    .modal-content { background: transparent; border: none; }

    .modal-body-custom {
      position: relative;
      border-radius: 20px;
      overflow: hidden;
      background: rgba(0,0,0,0.85);
      backdrop-filter: blur(14px);
      cursor: pointer;
      min-height: 72vh;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .fullscreen-img {
      max-width: 100%;
      max-height: 72vh;
      object-fit: contain;
      border-radius: 10px;
      animation: ${fadeUp} 0.25s ease;
    }

    .nav-arrow {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      background: rgba(0,0,0,0.6);
      color: white;
      width: 56px; height: 56px;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-size: 1.4em;
      cursor: pointer;
      border: 2px solid rgba(255,255,255,0.2);
      transition: all 0.25s;
      z-index: 1000;

      &:hover {
        background: rgba(199,112,240,0.75);
        border-color: #c770f0;
        transform: translateY(-50%) scale(1.1);
      }
      &.left  { left: 20px; }
      &.right { right: 20px; }
    }

    .image-counter {
      position: absolute;
      bottom: 18px; left: 50%;
      transform: translateX(-50%);
      background: rgba(0,0,0,0.65);
      color: white;
      padding: 6px 16px;
      border-radius: 20px;
      font-size: 0.85em;
      font-weight: 500;
      backdrop-filter: blur(6px);
    }

   

  /* ── Responsive ── */
  @media (max-width: 768px) {
    .project-img-wrapper { height: 180px; }
    .card-body { padding: 16px !important; }
    .card-title { font-size: 1.1em; }
    .project-buttons .btn { font-size: 0.8em; padding: 8px 10px; }
  }
`;

function ProjectCards(props) {
  const [showModal, setShowModal]     = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [saved, setSaved]             = useState(false);
  const navigate = useNavigate();

  const images = Array.isArray(props?.screenshots)
    ? props.screenshots
    : [props.screenshots].filter(Boolean);

  // Status badge helper
  const statusLabel = props.status || (props.live_url ? "live" : null);

  const prevImg = (e) => {
    e?.stopPropagation();
    setActiveImage((p) => (p === 0 ? images.length - 1 : p - 1));
  };
  const nextImg = (e) => {
    e?.stopPropagation();
    setActiveImage((p) => (p === images.length - 1 ? 0 : p + 1));
  };

  const isFeatured = props.status === "featured";

  return (
    <ProjectCardWrapper className={isFeatured ? "featured" : ""}>
      <Card className="project-card-view">

        {/* ── Image Section ── */}
        <div className="project-img-wrapper">
          {isFeatured && <div className="featured-ribbon" />}
          {statusLabel && (
            <span className={`status-badge ${statusLabel.toLowerCase()}`}>
              {isFeatured ? "⭐ Featured" : statusLabel}
            </span>
          )}

          <button
            className={`bookmark-btn ${saved ? "saved" : ""}`}
            onClick={(e) => { e.stopPropagation(); setSaved((s) => !s); }}
            title={saved ? "Remove bookmark" : "Bookmark"}
          >
            {saved ? <MdBookmark /> : <MdBookmarkBorder />}
          </button>

          <Card.Img
            src={images[activeImage] || images[0]}
            className="project-img"
          />

          {/* Hover overlay */}
          <div className="img-overlay">
            <div
              className="overlay-btn"
              title="Fullscreen"
              onClick={(e) => { e.stopPropagation(); setShowModal(true); }}
            >
              <FiZoomIn />
            </div>
            <div
              className="overlay-btn"
              title="View Details"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/projectDetails/${props.id}`, { state: props });
              }}
            >
              <MdOpenInNew />
            </div>
          </div>

          {images.length > 1 && (
            <>
              <FiChevronLeft className="mini-arrow left"  onClick={prevImg} />
              <FiChevronRight className="mini-arrow right" onClick={nextImg} />
              <div className="img-dots">
                {images.map((_, i) => (
                  <div
                    key={i}
                    className={`dot ${i === activeImage ? "active" : ""}`}
                    onClick={(e) => { e.stopPropagation(); setActiveImage(i); }}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* ── Card Body ── */}
        <Card.Body>
          {(props.duration || props.role) && (
            <div className="project-meta">
              {props.duration && (
                <div className="meta-item">
                  <FaRegClock /><span>{props.duration}</span>
                </div>
              )}
              {props.role && (
                <div className="meta-item">
                  <FaUserTie /><span>{props.role}</span>
                </div>
              )}
            </div>
          )}

          <Card.Title className="text-start">{props.projectName}</Card.Title>

          <Card.Text className="text-start">{props.short_description}</Card.Text>

          {/* Tech Stack Tags */}
          {props.tech_stack?.length > 0 && (
            <div className="tech-tags">
              {props.tech_stack.slice(0, 4).map((tech, i) => (
                <span key={i} className="tech-tag">{tech}</span>
              ))}
              {props.tech_stack.length > 4 && (
                <span className="tech-tag">+{props.tech_stack.length - 4}</span>
              )}
            </div>
          )}

          {/* Buttons */}
          <div className="project-buttons">
            <Button
              className="btn btn-primary"
              onClick={() => navigate(`/projectDetails/${props.id}`, { state: props })}
            >
              Details <MdOpenInNew />
            </Button>

            {props.live_url && (
              <Button className="btn btn-outline" href={props.live_url} target="_blank">
                Live <MdOpenInNew />
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* ── Fullscreen Modal ── */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered size="xl"
        backdrop="static"
        className="image-modal"
      >
        <Modal.Body
          className="p-0 modal-body-custom"
          onClick={() => setShowModal(false)}
        >
          <CloseModalButton
            className="modal-close"
            onClick={(e) => { e.stopPropagation(); setShowModal(false); }}
          >✕</CloseModalButton>

          {images.length > 1 && (
            <FiChevronLeft className="nav-arrow left" onClick={prevImg} />
          )}

          <img src={images[activeImage]} className="fullscreen-img" alt="project" />

          {images.length > 1 && (
            <>
              <div className="image-counter">{activeImage + 1} / {images.length}</div>
              <FiChevronRight className="nav-arrow right" onClick={nextImg} />
            </>
          )}
        </Modal.Body>
      </Modal>
    </ProjectCardWrapper>
  );
}

export default ProjectCards;